/**
 * dateUtils.ts — SimForge date utilities
 *
 * Ground truth: sim_date column (YYYY-MM-DD) on simulation_state and leagues.
 * Always use formatSimDate / formatSimDateShort when sim_date is available.
 * gameDayToDate is a fallback only.
 *
 * Season calendar:
 *   Regular season (status='active'):  day 1 = April 1  → day 153 = Sept 30
 *   Playoffs       (status='playoffs'): day 1 = Oct 1   → day 31  = Oct 31
 *   Off-season     (status='off_season'): frozen at Oct 31
 */

const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Parse YYYY-MM-DD safely without timezone shifts.
 * Avoids pre-1970 browser bugs with Date constructor.
 */
function parseSimDate(simDate: string | Date): { y: number; m: number; d: number } {
  if (typeof simDate !== 'string') {
    return { y: simDate.getUTCFullYear(), m: simDate.getUTCMonth(), d: simDate.getUTCDate() };
  }
  const parts = simDate.split('-').map(Number);
  return { y: parts[0], m: parts[1] - 1, d: parts[2] }; // m is 0-indexed
}

/**
 * Format sim_date as "October 15, 1900".
 * Use this everywhere a full date is needed.
 */
export function formatSimDate(simDate: string | Date | null | undefined, seasonYear?: number): string {
  if (!simDate) return seasonYear ? `Season ${seasonYear}` : '—';
  const { y, m, d } = parseSimDate(simDate);
  return `${MONTHS_LONG[m]} ${d}, ${y}`;
}

/**
 * Format sim_date as "Oct 15, 1900" (compact).
 */
export function formatSimDateShort(simDate: string | Date | null | undefined): string {
  if (!simDate) return '—';
  const { y, m, d } = parseSimDate(simDate);
  return `${MONTHS_SHORT[m]} ${d}, ${y}`;
}

/**
 * Get the display date for a league card.
 * Prefers sim_date; falls back to day calculation.
 */
export function leagueDateDisplay(league: {
  sim_date?: string | null;
  current_day?: number;
  season_year?: number;
  status?: string;
}): string {
  if (league.sim_date) return formatSimDateShort(league.sim_date);
  if (!league.current_day) return '—';
  const status = (league.status || 'active') as 'active' | 'in_season' | 'playoffs' | 'off_season' | 'pre_season';
  return gameDayToShortDate(league.current_day, league.season_year, status);
}

/**
 * Convert a game_day number to a Date.
 * Requires status to correctly map day 1 to April 1 (active) or Oct 1 (playoffs).
 */
export function gameDayToDate(
  gameDay: number,
  seasonYear: number = new Date().getFullYear(),
  status: 'active' | 'in_season' | 'playoffs' | 'off_season' | 'pre_season' = 'active'
): Date {
  if (status === 'off_season') {
    return new Date(Date.UTC(seasonYear, 9, 31)); // Oct 31
  }
  if (status === 'pre_season') {
    return new Date(Date.UTC(seasonYear, 2, 31)); // March 31
  }
  if (status === 'playoffs') {
    const oct1 = new Date(Date.UTC(seasonYear, 9, 1));
    oct1.setUTCDate(oct1.getUTCDate() + (gameDay - 1));
    return oct1;
  }
  const apr1 = new Date(Date.UTC(seasonYear, 3, 1));
  apr1.setUTCDate(apr1.getUTCDate() + (gameDay - 1));
  return apr1;
}

/**
 * Convert game_day to "Oct 14" format.
 * Pass status so playoffs days map to October, not April.
 */
export function gameDayToShortDate(
  gameDay: number,
  seasonYear?: number,
  status?: 'active' | 'in_season' | 'playoffs' | 'off_season' | 'pre_season'
): string {
  const date = gameDayToDate(gameDay, seasonYear, status);
  return `${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

export function gameDayToMediumDate(
  gameDay: number,
  seasonYear?: number,
  status?: 'active' | 'in_season' | 'playoffs' | 'off_season' | 'pre_season'
): string {
  return gameDayToShortDate(gameDay, seasonYear, status);
}

export function gameDayToFullDate(
  gameDay: number,
  seasonYear?: number,
  status?: 'active' | 'in_season' | 'playoffs' | 'off_season' | 'pre_season'
): string {
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const date = gameDayToDate(gameDay, seasonYear, status);
  return `${days[date.getUTCDay()]}, ${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

export function gameDayToDisplay(
  gameDay: number,
  seasonYear?: number,
  status?: 'active' | 'in_season' | 'playoffs' | 'off_season' | 'pre_season'
): string {
  return gameDayToShortDate(gameDay, seasonYear, status);
}
